import { useState } from "react";
import { post } from "@/lib/api";
import { usePersona } from "@/context/PersonaContext";
import { canPerform, allowedRoleNames } from "@/data/canvas";
import { KatlasReceiptCard } from "@/components/KatlasReceiptCard";
import { LayerTag } from "@/components/LayerTag";
import { ShieldCheck, Loader2, PlugZap, TriangleAlert, Info } from "lucide-react";

// The single governed micro-journey (Stage 3). Honest about node status.
export function GovernedActionPanel({ journey, refId, governed, action, disabledReason }) {
  const { persona } = usePersona();
  const [state, setState] = useState("idle"); // idle | signing | done | error
  const [receipt, setReceipt] = useState(null);
  const [err, setErr] = useState(null);

  const authorised = canPerform(persona.id, action);
  const blocked = Boolean(disabledReason);

  const sign = async () => {
    setState("signing");
    setErr(null);
    try {
      const res = await post("/mill/govern/sign", {
        journey,
        ref_id: refId,
        actor_name: persona.name,
        decision: "approve",
      });
      setReceipt(res.receipt);
      setState("done");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "object" ? detail : { error: "error", message: detail || "Signing failed." });
      setState("error");
    }
  };

  return (
    <div
      data-testid="governed-action-panel"
      className="rounded-2xl border border-dashed border-katlas-receipt/60 bg-[#FFFDF5] p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#B45309]" />
          <h3 className="font-display text-lg font-semibold text-mill-ink">{governed.title}</h3>
        </div>
        <LayerTag layer="governed" />
      </div>
      <p className="mt-2 text-sm leading-relaxed text-mill-muted">{governed.statement}</p>

      {state !== "done" && (
        <div className="mt-4 space-y-3">
          {blocked && (
            <p className="flex items-start gap-2 rounded-xl bg-mill-alt px-3 py-2 text-sm text-mill-muted" data-testid="governed-blocked">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              {disabledReason}
            </p>
          )}
          {!authorised && !blocked && (
            <p className="flex items-start gap-2 rounded-xl bg-mill-alt px-3 py-2 text-sm text-mill-muted" data-testid="governed-authority-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              Only a {allowedRoleNames(action)} may sign this. You are viewing as {persona.role} — switch persona to try it.
            </p>
          )}

          <button
            data-testid="governed-sign-button"
            disabled={!authorised || blocked || state === "signing"}
            onClick={sign}
            className="inline-flex items-center gap-2 rounded-md bg-katlas-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-katlas-blue-dark disabled:cursor-not-allowed disabled:opacity-40"
          >
            {state === "signing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {state === "signing" ? "Requesting receipt…" : "Request KATLAS receipt"}
          </button>
          <p className="text-xs text-mill-muted">
            Live signing is server-side only. No wallet is created for any real person. The receipt's status is always shown honestly.
          </p>
        </div>
      )}

      {state === "error" && err && (
        <div data-testid="governed-error" className="mt-4 rounded-xl border border-[#FDE68A] bg-[#FEF3C7] p-4">
          <div className="flex items-center gap-2 text-[#B45309]">
            {err.error === "katlas_not_yet_connected" ? <PlugZap className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
            <span className="text-sm font-semibold">
              {err.error === "katlas_not_yet_connected" ? "KATLAS not yet connected" : "Receipt not produced"}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-[#92400E]">{err.message}</p>
          <p className="mt-2 text-xs text-[#B45309]/80">
            The canvas fails honestly — no simulated or fake receipt is shown when the live node is unavailable.
          </p>
        </div>
      )}

      {state === "done" && receipt && (
        <div className="mt-4" data-testid="governed-receipt">
          <KatlasReceiptCard receipt={receipt} />
        </div>
      )}
    </div>
  );
}
